import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Troppe richieste. Riprova tra qualche minuto.");
      } else {
        toast.error(err.response?.data?.detail || "Errore. Riprova.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
      <div className="w-full max-w-md animate-fade-up" data-testid="forgot-password-page">
        <Link to="/login" className="flex items-center gap-3 mb-12 w-fit">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif-display text-2xl text-[#1A1A1A]">AI TricoProtocol</span>
            <span className="text-[10px] tracking-[0.25em] text-[#C5A059] uppercase mt-1">Professional Edition</span>
          </div>
        </Link>

        {!sent ? (
          <>
            <h1 className="font-serif-display text-4xl text-[#1A1A1A] mb-3 gold-underline inline-block">
              Recupero password
            </h1>
            <p className="text-[#737373] mb-10 mt-6">
              Inserisci la tua email: ti invieremo un link per impostare una nuova password.
            </p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all"
                    placeholder="nome@salone.it"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#1A1A1A] text-white font-medium hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50"
                data-testid="forgot-password-submit-button"
              >
                {loading ? "Invio in corso..." : "Invia link di reset"}
              </button>
            </form>

            <Link
              to="/login"
              className="mt-8 inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#C5A059] transition-colors"
              data-testid="forgot-password-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al login
            </Link>
          </>
        ) : (
          <div data-testid="forgot-password-confirmation">
            <div className="w-14 h-14 rounded-2xl bg-[#FAF6EE] flex items-center justify-center mb-6">
              <CheckCircle2 className="w-7 h-7 text-[#C5A059]" />
            </div>
            <h1 className="font-serif-display text-3xl text-[#1A1A1A] mb-4">
              Controlla la tua email
            </h1>
            <p className="text-[#737373] leading-relaxed mb-8">
              Se <span className="text-[#1A1A1A] font-medium">{email}</span> è registrata, ti abbiamo inviato un
              link per reimpostare la password. Il link scade tra <strong>1 ora</strong>.
            </p>
            <p className="text-xs text-[#A3A3A3] mb-8">
              Non hai ricevuto nulla? Controlla la cartella spam, oppure{" "}
              <button
                onClick={() => setSent(false)}
                className="text-[#C5A059] hover:underline"
                data-testid="forgot-password-retry-button"
              >
                riprova
              </button>.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
